/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Captures the 6-dimension Perception Impact Score for an event close.
 * Replaces the legacy "attendance success" model.
 */
export default function PerceptionImpactForm({ eventId, onClosed }) {
  const [dims, setDims] = useState([]);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/terra-growth/reference/perception-impact`)
      .then(r => setDims(r.data.dimensions || []))
      .catch(() => setDims([]));
  }, []);

  const composite = dims.length
    ? Math.round((dims.reduce((s, d) => s + (parseFloat(scores[d.code]) || 0), 0)
                  / dims.length) * 10) / 10
    : 0;

  const submit = async () => {
    if (dims.some(d => scores[d.code] === undefined || scores[d.code] === '')) {
      toast.error('All 6 dimensions need a score before close.');
      return;
    }
    setBusy(true);
    try {
      const payload = { notes: notes || null };
      dims.forEach(d => { payload[d.code] = parseFloat(scores[d.code]); });
      const r = await axios.post(`${API}/terra-growth/events/${eventId}/close`, payload);
      toast.success(`Event closed · impact composite ${r.data?.perception_impact_score?.composite ?? '—'}`);
      onClosed && onClosed(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not close event.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="perception-impact-form" className="rounded-2xl border border-[#E5D6BE] bg-[#FAF1E1] p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#8C6239]" />
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C6239]">
            Close event · Perception Impact Score
          </p>
        </div>
        <span className="text-[#5C3D1F] font-['Outfit'] text-2xl tabular-nums">{composite || '—'}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {dims.map(d => (
          <label key={d.code} className="flex flex-col gap-1">
            <span className="text-[11px] font-['Work_Sans'] text-[#5C3D1F]">{d.label}</span>
            <Input
              type="number" min="0" max="100"
              value={scores[d.code] ?? ''}
              onChange={(e) => setScores(s => ({ ...s, [d.code]: e.target.value }))}
              placeholder="0–100" className="h-9 text-sm"
              data-testid={`impact-input-${d.code}`}
            />
          </label>
        ))}
      </div>

      <Input
        value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes — what shifted? Who emerged?"
        className="mt-3 h-9 text-sm" data-testid="impact-notes-input"
      />

      <Button
        onClick={submit} disabled={busy}
        data-testid="impact-close-event-btn"
        className="mt-4 bg-[#8C6239] hover:bg-[#75502F] text-white text-xs rounded-full px-4"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
        Close & lock impact
      </Button>
    </div>
  );
}
